import React from 'react';
import { CircleDot } from 'lucide-react';
import { useChatInput } from '../../contexts/ChatContext';

export const AnalysisLoading: React.FC = () => {
  const { deepAnalysis } = useChatInput();

  return (
    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-lg p-4 transition-colors">
      {/* Football field container */}
      <div className="relative w-full max-w-[280px] mx-auto h-24 bg-gradient-to-b from-green-500 to-green-600 dark:from-green-600 dark:to-green-700 rounded-lg overflow-hidden shadow-inner">
        {/* Field markings */}
        <div className="absolute inset-0">
          {/* Center circle */}
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-8 h-8 border-2 border-white/30 rounded-full" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1 h-1 bg-white/30 rounded-full" />
          
          {/* Center line */}
          <div className="absolute top-0 left-1/2 h-full w-0.5 bg-white/30" />
          
          {/* Penalty areas */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-8 h-12 border-2 border-l-0 border-white/30" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-8 h-12 border-2 border-r-0 border-white/30" />
          
          {/* Goal posts */}
          <div className="absolute left-0 top-1/2 -translate-y-1/2 w-1 h-6 border-2 border-l-0 border-white/50" />
          <div className="absolute right-0 top-1/2 -translate-y-1/2 w-1 h-6 border-2 border-r-0 border-white/50" />
        </div>

        {/* Field texture */}
        <div className="absolute inset-0" 
          style={{
            backgroundImage: `
              repeating-linear-gradient(
                90deg, 
                transparent, 
                transparent 15px, 
                rgba(255,255,255,0.03) 15px, 
                rgba(255,255,255,0.03) 30px
              )
            `
          }} 
        />

        {/* Players */}
        <div className="absolute left-[8%] top-1/2 -translate-y-1/2 animate-playerRun">
          {/* Player silhouette */}
          <div className="w-3 h-6 bg-blue-600/80 rounded-full relative">
            <div className="absolute -right-1 top-1/2 w-3 h-1 bg-blue-600/80 rounded-full origin-left animate-playerKick" />
          </div>
        </div>

        <div className="absolute right-[8%] top-1/2 -translate-y-1/2 animate-playerRun" style={{ animationDelay: '-0.5s' }}>
          {/* Player silhouette */}
          <div className="w-3 h-6 bg-red-600/80 rounded-full relative">
            <div className="absolute -left-1 top-1/2 w-3 h-1 bg-red-600/80 rounded-full origin-right animate-playerKick" style={{ animationDelay: '-0.5s' }} />
          </div>
        </div>

        {/* Animated ball */}
        <div className="absolute animate-bouncingBall">
          <div className="relative">
            <CircleDot className="w-3 h-3 text-white drop-shadow-lg" />
            <div className="absolute -inset-1">
              <div className="w-5 h-5 rounded-full bg-white/10 animate-pulse" />
            </div>
          </div>
        </div>

        {/* Ball shadow */}
        <div 
          className="absolute bottom-2 left-1/2 -translate-x-1/2 w-3 h-1 bg-black/10 rounded-full blur-[1px]"
          style={{
            animation: 'shadowMove 4s cubic-bezier(0.45, 0, 0.55, 1) infinite'
          }}
        />

        {/* Data gathering particles */}
        <div className="absolute inset-0 overflow-hidden">
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute w-0.5 h-0.5 bg-white/40 rounded-full animate-floatUp"
              style={{
                left: `${10 + i * 12}%`,
                animationDelay: `${i * 0.25}s`
              }}
            />
          ))}
        </div>
      </div>

      {/* Loading text */}
      <div className="text-center mt-3 space-y-1.5">
        {/* Deep Analysis badge is hidden */}
        <h3 className="font-medium text-gray-900 dark:text-white text-sm px-4">
          Good things take time! We're analyzing the data — sit tight, it won't be long!
        </h3>
        <div className="flex items-center justify-center gap-1">
          <div className="w-1 h-1 bg-blue-500 dark:bg-blue-400 rounded-full animate-bounce [animation-delay:-0.3s]"></div>
          <div className="w-1 h-1 bg-blue-500 dark:bg-blue-400 rounded-full animate-bounce [animation-delay:-0.15s]"></div>
          <div className="w-1 h-1 bg-blue-500 dark:bg-blue-400 rounded-full animate-bounce"></div>
        </div>
      </div>
    </div>
  );
};